/* LessorDetail.js – visual alinhado ao BuyerDetail */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link as RouterLink } from 'react-router-dom';
import api from '../api';

// Shadcn UI Components
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Spinner } from '../components/ui/spinner';
import { Badge } from '../components/ui/badge';

/* getter seguro */
const F = (data, path, t) => {
  if (!path) return '-';
  const v = path.split('.').reduce((o, k) => o?.[k], data);
  if (v === undefined || v === null || v === '') return '-';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '-';
  if (typeof v === 'boolean') return v ? t('common.yes') : t('common.no');
  return v;
};

export default function LessorDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState("basic");

  useEffect(() => { 
    api.get(`/locadoras/${id}`).then(r => setData(r.data)); 
  }, [id]);

  if (!data) {
    return (
      <div className="container mx-auto px-4 mt-4 mb-6">
        <div className="min-h-[400px] flex flex-col items-center justify-center gap-4 bg-background border rounded-lg p-6">
          <Spinner className="h-10 w-10 text-primary" />
          <p className="text-muted-foreground">
            {t('lessor.detail.loading')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-6xl">
      <Card className="overflow-hidden">
        <div className="p-6 border-b">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <h1 className="text-3xl font-bold tracking-tight">
              {data.nomeFantasia}
            </h1>
            
            <Button 
              asChild
              size="lg"
              className="font-semibold"
            >
              <RouterLink to={`/admin/locadoras/${id}/edit`}>
                {t('common.edit')}
              </RouterLink>
            </Button>
          </div>

          <Tabs 
            value={activeTab} 
            onValueChange={setActiveTab} 
            className="w-full"
          >
            <TabsList className="mb-2">
              <TabsTrigger value="basic">{t('lessor.tabs.basic')}</TabsTrigger>
              <TabsTrigger value="representatives">{t('lessor.tabs.representatives')}</TabsTrigger>
              <TabsTrigger value="locations">{t('lessor.tabs.locations')}</TabsTrigger>
              <TabsTrigger value="operational">{t('lessor.tabs.operational')}</TabsTrigger>
              <TabsTrigger value="financial">{t('lessor.tabs.financial')}</TabsTrigger>
              <TabsTrigger value="commercial">{t('lessor.tabs.commercial')}</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="p-6">
          <Tabs 
            value={activeTab} 
            onValueChange={setActiveTab} 
            className="w-full"
          >
          {/* Tab: Informações Básicas */}
          <TabsContent value="basic">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                [t('lessor.fields.nomeFantasia'), 'nomeFantasia'], 
                [t('lessor.fields.razaoSocial'), 'razaoSocial'],
                [t('lessor.fields.cnpj'), 'cnpj'], 
                [t('lessor.detail.inscricaoEst'), 'inscricaoEstadual'],
                [t('lessor.detail.grupo'), 'grupoEmpresarial']
              ].map(([label, path]) => (
                <div key={path} className="p-4 bg-muted/40 rounded-md border">
                  <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">
                    {label}
                  </span>
                  <p className="mt-1 font-medium">
                    {F(data, path, t)}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-8">
              <h3 className="text-lg font-semibold mb-4">{t('lessor.sections.address')}</h3>

              <Card className="p-6 bg-muted/40">
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
                  {[
                    [t('lessor.address.cep'), 'endereco.cep', 3], 
                    [t('lessor.address.logradouro'), 'endereco.logradouro', 5],
                    [t('lessor.address.numero'), 'endereco.numero', 2], 
                    [t('lessor.address.complemento'), 'endereco.complemento', 2],
                    [t('lessor.address.bairro'), 'endereco.bairro', 3], 
                    [t('lessor.address.cidade'), 'endereco.cidade', 4], 
                    [t('lessor.address.estado'), 'endereco.estado', 2]
                  ].map(([label, path, cols]) => (
                    <div key={path} className={`col-span-${cols}`}>
                      <span className="text-xs text-muted-foreground block mb-1">
                        {label}
                      </span>
                      <p className="font-medium">
                        {F(data, path, t)}
                      </p>
                    </div>
                  ))}
                </div>
              </Card>
            </div>

            <div className="mt-8">
              <h3 className="text-lg font-semibold mb-4">{t('lessor.sections.mainContact')}</h3>

              <Card className="p-6 bg-muted/40">
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                  {[
                    ['nome', t('lessor.contact.nome')],
                    ['telefone', t('lessor.contact.telefone')],
                    ['celular', t('lessor.contact.celular')],
                    ['email', t('lessor.contact.email')]
                  ].map(([key, label]) => (
                    <div key={key}>
                      <span className="text-xs text-muted-foreground block mb-1">
                        {label}
                      </span>
                      <p className="font-medium">
                        {F(data, `contatoPrincipal.${key}`, t)}
                      </p>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* TAB 1: Representantes */}
          <TabsContent value="representatives">
            <h3 className="text-lg font-semibold mb-4">{t('lessor.sections.representatives')}</h3>

            {data.representantes?.length ? 
              data.representantes.map((rep, index) => (
                <Card 
                  key={index} 
                  className="mb-4 p-6 bg-muted/40 hover:border-primary transition-colors"
                >
                  <h4 className="text-base font-semibold text-primary mb-4">
                    {rep.nome || '-'}
                  </h4>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {[
                      [t('lessor.representative.departamento'), rep.departamento],
                      [t('lessor.representative.celular'), rep.celular],
                      [t('lessor.representative.email'), rep.email]
                    ].map(([label, value], idx) => (
                      <div key={idx}>
                        <span className="text-xs text-muted-foreground block mb-1">
                          {label}
                        </span>
                        <p className="font-medium">
                          {value || '-'}
                        </p>
                      </div>
                    ))}
                  </div>
                </Card>
              ))
              : (
                <p className="text-muted-foreground">
                  {t('lessor.detail.noRepresentatives')}
                </p>
              )
            }
          </TabsContent>

          {/* TAB 2: Financeiro */}
          <TabsContent value="financial">
            <div>
              <h2 className="text-lg font-bold text-primary mb-4">
                {t('lessor.detail.financialInfo')}
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  [t('lessor.detail.accountName'), 'financeiro.nomeConta'],
                  [t('lessor.detail.bank'), 'financeiro.banco'],
                  [t('lessor.detail.agency'), 'financeiro.agencia'],
                  [t('lessor.detail.account'), 'financeiro.conta'],
                  [t('lessor.detail.paymentTerm'), 'financeiro.prazoPgto']
                ].map(([l, p]) => (
                  <Card key={p} className="border rounded-lg">
                    <CardContent>
                      <h3 className="text-sm font-medium text-muted-foreground mb-1">
                        {l}
                      </h3>
                      <p className="text-lg font-semibold text-primary">
                        {F(data, p, t)}
                      </p>
                    </CardContent>
                  </Card>
                ))}

                <div className="col-span-1 sm:col-span-2 lg:col-span-3">
                  <Card className="border rounded-lg">
                    <CardContent>
                      <h3 className="text-sm font-medium text-muted-foreground mb-1">
                        {t('lessor.detail.paymentMethods')}
                      </h3>
                      <p className="text-lg font-semibold text-primary">
                        {F(data, 'financeiro.formas', t)}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* TAB 3: Comercial */}
          <TabsContent value="commercial">
            <div>
              <h2 className="text-lg font-bold text-primary mb-4">
                {t('lessor.detail.commercialInfo')}
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  [t('lessor.detail.maxDiscount'), 'comercial.descontoMax'],
                  [t('lessor.detail.negotiationType'), 'comercial.tipoNegociacao'],
                  [t('lessor.detail.saleType'), 'comercial.tipoVenda'],
                  [t('lessor.detail.acceptsBelowPrice'), 'comercial.aceitaAbaixo'],
                  [t('lessor.detail.atpvTime'), 'comercial.tempoATPV']
                ].map(([l, p]) => (
                  <Card key={p} className="border rounded-lg">
                    <CardContent>
                      <h3 className="text-sm font-medium text-muted-foreground mb-1">
                        {l}
                      </h3>
                      <p className="text-lg font-semibold text-primary">
                        {F(data, p, t)}
                      </p>
                    </CardContent>
                  </Card>
                ))}

                <div className="col-span-1 sm:col-span-2">
                  <Card className="border rounded-lg">
                    <CardContent>
                      <h3 className="text-sm font-medium text-muted-foreground mb-1">
                        {t('lessor.detail.additionalInfo')}
                      </h3>
                      <p className="text-lg font-semibold text-primary whitespace-pre-wrap">
                        {F(data, 'comercial.adicionais', t)}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* TAB 4: Pátios */}
          <TabsContent value="locations">
            <h3 className="text-lg font-semibold mb-4">{t('lessor.sections.locations')}</h3>

            {data.patios?.length ? 
              data.patios.map((patio, index) => (
                <Card 
                  key={index} 
                  className="mb-4 p-6 bg-muted/40 hover:border-primary transition-colors"
                >
                  <h4 className="text-base font-semibold text-primary mb-4">
                    Pátio #{index + 1}
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-xs text-muted-foreground block mb-1">CEP</span>
                      <p className="font-medium">{patio.cep || '-'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground block mb-1">Número</span>
                      <p className="font-medium">{patio.numero || '-'}</p>
                    </div>
                    <div className="md:col-span-2">
                      <span className="text-xs text-muted-foreground block mb-1">Logradouro</span>
                      <p className="font-medium">{patio.logradouro || '-'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground block mb-1">Complemento</span>
                      <p className="font-medium">{patio.complemento || '-'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground block mb-1">Bairro</span>
                      <p className="font-medium">{patio.bairro || '-'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground block mb-1">Cidade</span>
                      <p className="font-medium">{patio.cidade || '-'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground block mb-1">Estado</span>
                      <p className="font-medium">{patio.estado || '-'}</p>
                    </div>
                  </div>
                </Card>
              ))
              : (
                <p className="text-muted-foreground">
                  {t('lessor.detail.noLocations')}
                </p>
              )
            }
          </TabsContent>

          {/* Tab: Dados Operacionais */}
          <TabsContent value="operational">
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold mb-4">{t('lessor.sections.segments')}</h3>
                <Card className="p-6 bg-muted/40">
                  <div className="flex flex-wrap gap-2">
                    {data.segmentos?.length ?
                      data.segmentos.map((segmento) => (
                        <Badge key={segmento} variant="secondary" className="px-3 py-1">
                          {segmento === 'rental' && t('lessor.segments.rental')}
                          {segmento === 'fleet' && t('lessor.segments.fleet')}
                          {segmento === 'sales' && t('lessor.segments.sales')}
                        </Badge>
                      ))
                      : <p className="text-muted-foreground">{t('lessor.detail.noSegments')}</p>
                    }
                  </div>
                </Card>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4">{t('lessor.sections.regions')}</h3>
                <Card className="p-6 bg-muted/40">
                  <div className="flex flex-wrap gap-2">
                    {data.regioes?.length ?
                      data.regioes.map((regiao) => (
                        <Badge key={regiao} variant="secondary" className="px-3 py-1">
                          {regiao === 'sul' && t('lessor.regions.sul')}
                          {regiao === 'sudeste' && t('lessor.regions.sudeste')}
                          {regiao === 'centrooeste' && t('lessor.regions.centrooeste')}
                          {regiao === 'nordeste' && t('lessor.regions.nordeste')}
                          {regiao === 'norte' && t('lessor.regions.norte')}
                        </Badge>
                      ))
                      : <p className="text-muted-foreground">{t('lessor.detail.noRegions')}</p>
                    }
                  </div>
                </Card>
              </div>

              {data.observacoes && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">{t('lessor.sections.observations')}</h3>
                  <Card className="p-6 bg-muted/40">
                    <p className="whitespace-pre-wrap">{data.observacoes}</p>
                  </Card>
                </div>
              )}
            </div>
          </TabsContent>
          </Tabs>
        </div>
      </Card>
    </div>
  );
}
